import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import {restaurantApi} from '../services/api';
import {useMerchant} from '../context/MerchantContext';
import {DeliveryMethod, Restaurant} from '../types';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  color: #333;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const PageDescription = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 2rem;
  text-align: center;
`;

const FormContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #e74c3c;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #e74c3c;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 0.5rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const RadioInput = styled.input`
  cursor: pointer;
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 1rem;

  &:hover {
    background-color: #c0392b;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  background-color: #2ecc71;
  color: white;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const RestaurantInfoContainer = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`;

const RestaurantName = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const RestaurantAddress = styled.p`
  font-size: 1rem;
  color: #666;
`;

const OrderSchedulingPage: React.FC = () => {
  const { restaurantId: paramId } = useParams<{ restaurantId?: string }>();
  const { merchantId: contextId, isLoading: isMerchantLoading } = useMerchant();
  const restaurantId = paramId || contextId || 'default';
  
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>(DeliveryMethod.DELIVERY);
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [scheduledTime, setScheduledTime] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  // Function to fetch restaurant data
  const fetchRestaurant = useCallback(async () => {
    try {
      const data = await restaurantApi.getRestaurant(restaurantId);
      setRestaurant(data);
    } catch (err) {
      console.error('Error fetching restaurant:', err);
    }
  }, [restaurantId]);

  // Fetch data on component mount
  useEffect(() => {
    fetchRestaurant();
    
    // Set default scheduled date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduledDate(tomorrow.toISOString().split('T')[0]);
    
    // Set default scheduled time to noon
    setScheduledTime('12:00');
  }, [fetchRestaurant]);

  // Generate available time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 10; hour <= 21; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        slots.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return slots;
  };

  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }
    
    if (!phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    } else if (!/^\d{10,11}$/.test(phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Telefone inválido';
    }
    
    if (deliveryMethod === DeliveryMethod.DELIVERY && !address.trim()) {
      newErrors.address = 'Endereço é obrigatório para entrega';
    }
    
    if (!scheduledDate) {
      newErrors.scheduledDate = 'Data é obrigatória';
    } else {
      const selectedDate = new Date(scheduledDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.scheduledDate = 'A data não pode ser no passado';
      }
    }
    
    if (!scheduledTime) {
      newErrors.scheduledTime = 'Horário é obrigatório';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // In a real application, this would send the order to the backend
      // For demonstration purposes, we'll just simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSuccess(true);
      
      // Reset form
      setDeliveryMethod(DeliveryMethod.DELIVERY);
      setScheduledDate('');
      setScheduledTime('');
      setName('');
      setPhone('');
      setAddress('');
      setNotes('');
      
      // Redirect to menu after a delay
      setTimeout(() => {
        navigate(`/menu/${restaurantId}`);
      }, 3000);
    } catch (error) {
      console.error('Error submitting order:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isMerchantLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          Carregando informações do restaurante...
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer>
        <PageTitle>Agendar Pedido</PageTitle>
        <PageDescription>
          Agende seu pedido para retirada ou entrega em um horário específico.
        </PageDescription>
        
        {restaurant && (
          <RestaurantInfoContainer>
            <RestaurantName>{restaurant.name}</RestaurantName>
            {restaurant.address && (
              <RestaurantAddress>
                {`${restaurant.address.street}, ${restaurant.address.number} - ${restaurant.address.neighborhood}, ${restaurant.address.city}`}
              </RestaurantAddress>
            )}
          </RestaurantInfoContainer>
        )}
        
        {success && (
          <SuccessMessage>
            Seu pedido foi agendado com sucesso! Você será redirecionado para o cardápio em instantes.
          </SuccessMessage>
        )}
        
        <FormContainer>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Método de Entrega</Label>
              <RadioGroup>
                <RadioLabel>
                  <RadioInput 
                    type="radio" 
                    name="deliveryMethod" 
                    value={DeliveryMethod.DELIVERY}
                    checked={deliveryMethod === DeliveryMethod.DELIVERY}
                    onChange={() => setDeliveryMethod(DeliveryMethod.DELIVERY)}
                  />
                  Entrega
                </RadioLabel>
                <RadioLabel>
                  <RadioInput 
                    type="radio" 
                    name="deliveryMethod" 
                    value={DeliveryMethod.TAKEAWAY}
                    checked={deliveryMethod === DeliveryMethod.TAKEAWAY}
                    onChange={() => setDeliveryMethod(DeliveryMethod.TAKEAWAY)}
                  />
                  Retirada
                </RadioLabel>
              </RadioGroup>
            </FormGroup>
            
            <FormGroup>
              <Label>Data do Agendamento</Label>
              <Input 
                type="date" 
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.scheduledDate && <ErrorMessage>{errors.scheduledDate}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label>Horário do Agendamento</Label>
              <Select 
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
              >
                <option value="">Selecione um horário</option>
                {generateTimeSlots().map((slot) => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </Select>
              {errors.scheduledTime && <ErrorMessage>{errors.scheduledTime}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label>Nome Completo</Label>
              <Input 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome completo"
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </FormGroup>
            
            <FormGroup>
              <Label>Telefone</Label>
              <Input 
                type="tel" 
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(00) 00000-0000"
              />
              {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
            </FormGroup>
            
            {deliveryMethod === DeliveryMethod.DELIVERY && (
              <FormGroup>
                <Label>Endereço de Entrega</Label>
                <Input 
                  type="text" 
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Rua, número, complemento, bairro, cidade"
                />
                {errors.address && <ErrorMessage>{errors.address}</ErrorMessage>}
              </FormGroup>
            )}
            
            <FormGroup>
              <Label>Observações (opcional)</Label>
              <Input 
                as="textarea" 
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Instruções especiais para o seu pedido"
              />
            </FormGroup>
            
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Agendando...' : 'Agendar Pedido'}
            </Button>
          </form>
        </FormContainer>
      </PageContainer>
    </Layout>
  );
};

export default OrderSchedulingPage;